import React, { useState, useEffect } from 'react';
import {
  Flex, Heading, Text, Icon, Box, Button, Badge,
  SimpleGrid, useColorModeValue, IconButton, Spinner,
  useToast, FormLabel, Input, Textarea,
} from '@chakra-ui/react';
import { MdSend, MdSave, MdArrowBack, MdBuild, MdNotes, MdLock } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db, storage } from '../../../firebase';
import { doc, getDoc, setDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { gerarChaveFoto, enqueuePhoto } from '../../../utils/photoQueue';
import { syncPendingPhotos } from '../../../utils/photoSync';
import Card from 'components/card/Card.js';
import TipoServicoField from 'components/fields/TipoServicoField';
import TesteDeFumacaForm, { FUMAÇA_PADRAO } from 'components/forms/TesteDeFumacaForm';
import TesteCoranteForm, { CORANTE_PADRAO } from 'components/forms/TesteCoranteForm';

// Remove campos com valor `undefined` recursivamente — Firestore rejeita undefined.
function stripUndefined(obj) {
  if (obj === null || obj === undefined) return obj ?? null;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  if (typeof obj === 'object') {
    // Preserva Firestore Timestamps, FieldValues (serverTimestamp, deleteField, etc.)
    if (typeof obj.toDate === 'function') return obj;
    if (obj.constructor && obj.constructor.name !== 'Object') return obj;
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)])
    );
  }
  return obj;
}

function gerarProtocolo() {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const rnd = Math.floor(1000 + Math.random() * 9000);
  return `OS-${yy}${mm}${dd}-${rnd}`;
}

// ── Comprime imagem antes do upload (reduz de ~5-15 MB → ~200-400 KB) ─────────
function compressImage(file, maxPx = 1280, quality = 0.78) {
  return new Promise((resolve) => {
    // HEIC/HEIF não são renderizáveis via Canvas no Chrome/Android — faz upload direto
    if (!file || !file.type.startsWith('image/') ||
        file.type === 'image/heic' || file.type === 'image/heif') {
      resolve(file);
      return;
    }
    const img = new window.Image();
    const blobUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const w = Math.round(img.width  * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve(file); };
    img.src = blobUrl;
  });
}

function FL({ children, required, color }) {
  return (
    <FormLabel fontSize="sm" fontWeight="500" color={color} mb="6px">
      {children}{required && <Text as="span" color="red.400" ms="1">*</Text>}
    </FormLabel>
  );
}

export default function RegistrarAtendimento() {
  const toast      = useToast();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const osId       = searchParams.get('id');

  const textColor  = useColorModeValue('navy.700', 'white');
  const subText    = useColorModeValue('gray.500', 'gray.400');
  const inputBg    = useColorModeValue('white', 'whiteAlpha.100');
  const cardBg     = useColorModeValue('white', 'navy.800');
  const cardBorder = useColorModeValue('gray.100', 'whiteAlpha.100');

  const [numeroOS,    setNumeroOS]    = useState('');
  const [tipoServico, setTipoServico] = useState('');
  const [tipoOutros,  setTipoOutros]  = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [status,      setStatus]      = useState('rascunho');
  const [saving,      setSaving]      = useState(false);
  const [loadingOS,   setLoadingOS]   = useState(!!osId);
  const [dadosEspecificos, setDadosEspecificos] = useState(FUMAÇA_PADRAO());
  const [protocolo,   setProtocolo]   = useState(gerarProtocolo());
  const [hora,        setHora]        = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [dataServico, setDataServico] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  });


  useEffect(() => {
    if (!osId) return;
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'ordens', osId));
        if (snap.exists()) {
          const d = snap.data();
          setNumeroOS(d.numeroOS || '');
          setTipoServico(d.tipoServico || '');
          setTipoOutros(d.tipoOutros || '');
          setObservacoes(d.observacoes || '');
          setStatus(d.status || 'rascunho');
          setProtocolo(d.protocolo || protocolo);
          setHora(d.hora || '');
          if (d.dataServico) setDataServico(d.dataServico);

          if (d.dadosEspecificos) setDadosEspecificos(d.dadosEspecificos);
        }
      } catch (e) {
        toast({ title: 'Erro ao carregar OS', description: e.message, status: 'error' });
      } finally {
        setLoadingOS(false);
      }
    }
    load();
  }, [osId]);



  // Upload individual de uma foto do dadosEspecificos.
  // Sempre tenta o Storage primeiro. Se falhar por rede (online ou offline),
  // salva o blob no IndexedDB e retorna { idbKey } para sync posterior.
  // Isso garante funcionamento mesmo quando navigator.onLine mente (Android 4G sem sinal).
  const uploadFotoEspecifica = async (uid, id, foto) => {
    // Foto já processada (tem URL ou idbKey) — devolve sem undefined
    if (!foto || !foto.file) {
      if (!foto) return null;
      const resultado = { legenda: foto.legenda || '' };
      if (foto.url)    resultado.url    = foto.url;
      if (foto.idbKey) resultado.idbKey = foto.idbKey;
      return resultado;
    }

    const compressed  = await compressImage(foto.file);
    const storagePath = `ordens/${uid}/${id}/esp_${Date.now()}.jpg`;

    if (navigator.onLine) {
      try {
        const storRef = ref(storage, storagePath);
        await uploadBytes(storRef, compressed, { contentType: compressed.type || 'image/jpeg' });
        return { url: await getDownloadURL(storRef), legenda: foto.legenda || '' };
      } catch {
        // Falhou (rede instável) → cai para a fila
      }
    }

    // Offline ou upload falhou → IndexedDB
    const key = gerarChaveFoto();
    await enqueuePhoto(key, compressed, { storagePath, legenda: foto.legenda || '', osId: id });
    // Nunca inclui campos undefined — Firestore rejeita
    return { idbKey: key, legenda: foto.legenda || '', url: null };
  };

  // Upload recursivo de todas as fotos dentro do dadosEspecificos
  const uploadDadosEspecificos = async (uid, id, dados) => {
    if (!dados) return dados;
    const d = { ...dados };

    // Singularidades
    if (Array.isArray(d.singularidades)) {
      d.singularidades = await Promise.all(
        d.singularidades.map(async (s) => ({
          ...s,
          fotoInicio: await uploadFotoEspecifica(uid, id, s.fotoInicio),
          fotoFim:    await uploadFotoEspecifica(uid, id, s.fotoFim),
        }))
      );
    }

    // Boca de Lobo (legado)
    if (Array.isArray(d.bocaFotos)) {
      d.bocaFotos = await Promise.all(
        d.bocaFotos.map((f) => uploadFotoEspecifica(uid, id, f))
      );
    }

    // Imóveis (legado)
    if (Array.isArray(d.imoveis)) {
      d.imoveis = await Promise.all(
        d.imoveis.map(async (im) => ({
          ...im,
          fotoFachada:    await uploadFotoEspecifica(uid, id, im.fotoFachada),
          fotoOcorrencia: await uploadFotoEspecifica(uid, id, im.fotoOcorrencia),
        }))
      );
    }

    // Ocorrências (Teste de Fumaça / Inspeção de Rede)
    if (Array.isArray(d.ocorrencias)) {
      d.ocorrencias = await Promise.all(
        d.ocorrencias.map(async (oc) => ({
          ...oc,
          fotos: Array.isArray(oc.fotos)
            ? await Promise.all(oc.fotos.map((f) => uploadFotoEspecifica(uid, id, f)))
            : oc.fotos,
        }))
      );
    }

    // Fotos genéricas (Teste de Corante / Inspeção de Ramal)
    if (Array.isArray(d.fotos)) {
      d.fotos = await Promise.all(d.fotos.map((f) => uploadFotoEspecifica(uid, id, f)));
    }

    // Assinatura do cliente (Teste de Corante) — base64 → Storage URL
    // Tenta upload; se falhar por rede, mantém base64 — photoSync converte depois.
    if (d.assinaturaCliente && d.assinaturaCliente.startsWith('data:')) {
      if (navigator.onLine) {
        try {
          const res     = await fetch(d.assinaturaCliente);
          const blob    = await res.blob();
          const storRef = ref(storage, `ordens/${uid}/${id}/assinatura_cliente.png`);
          await uploadBytes(storRef, blob);
          d.assinaturaCliente = await getDownloadURL(storRef);
        } catch {
          // falha de rede — mantém base64, photoSync converte quando voltar a rede
        }
      }
      // se offline ou se falhou, d.assinaturaCliente permanece como base64
    }

    // mapaCampo é dado JSON puro (sem arquivos)

    return d;
  };

  const salvar = async (novoStatus) => {
    if (status === 'aprovada') {
      toast({ title: 'OS bloqueada', description: 'Esta OS foi aprovada e não pode mais ser alterada pelo técnico.', status: 'warning', duration: 3500 }); return;
    }
    if (!tipoServico) {
      toast({ title: 'Selecione o tipo de serviço.', status: 'warning' }); return;
    }
    if (tipoServico === 'Outros' && !tipoOutros.trim()) {
      toast({ title: 'Descreva o tipo de serviço.', status: 'warning' }); return;
    }
    const user = auth.currentUser;
    if (!user) {
      toast({
        title: 'Sessão expirada',
        description: 'Faça login novamente para continuar.',
        status: 'warning',
        duration: 3000,
      });
      navigate('/auth/sign-in/default', { replace: true });
      return;
    }
    setSaving(true);
    const toastId = 'upload-progress';
    try {
      const id = osId || doc(collection(db, 'ordens')).id;

      // Nome do técnico: tenta Firestore (online) ou cache local (offline)
      let tecnicoNomeReal = localStorage.getItem('pg_tecnico_nome') || user.displayName || user.email;
      if (navigator.onLine) {
        try {
          const userSnap = await getDoc(doc(db, 'users', user.uid));
          const ud       = userSnap.exists() ? userSnap.data() : {};
          tecnicoNomeReal = ud.firstName && ud.lastName
            ? `${ud.firstName} ${ud.lastName}`
            : ud.displayName || user.displayName || user.email;
          localStorage.setItem('pg_tecnico_nome', tecnicoNomeReal);
        } catch { /* usa cache */ }
      }

      // Conta fotos novas para feedback ao usuário
      const totalFotos = (dadosEspecificos?.fotos || []).filter(f => f?.file).length;
      if (totalFotos > 0) {
        toast({ id: toastId, title: `Processando ${totalFotos} foto${totalFotos > 1 ? 's' : ''}...`, description: 'Comprimindo e enviando imagens.', status: 'loading', duration: null, isClosable: false });
      }

      const dadosEspSalvos = (
        tipoServico === 'Teste de Fumaça' ||
        tipoServico === 'Inspeção de Rede' ||
        tipoServico === 'Testes de Corante/Inspeção Ramal'
      )
        ? stripUndefined(await uploadDadosEspecificos(user.uid, id, dadosEspecificos))
        : null;
      // Extrai lat/lng do dadosEspecificos para a raiz do documento (PainelAdmin usa os.lat/os.lng)
      const coordsRaiz = dadosEspSalvos?.lat != null
        ? { lat: dadosEspSalvos.lat, lng: dadosEspSalvos.lng }
        : {};

      // Promove endereço do dadosEspecificos para a raiz para permitir busca no PainelAdmin
      // Corante: enderecoImovel | Fumaça: enderecoInspecionado
      const enderecoRaiz = dadosEspSalvos?.enderecoImovel
        || dadosEspSalvos?.enderecoInspecionado || null;

      await setDoc(doc(db, 'ordens', id), stripUndefined({
        protocolo,
        numeroOS: numeroOS.trim() || null,
        hora,
        dataServico,

        cliente: 'SABESP',
        tecnicoUid: user.uid,
        tecnicoNome: tecnicoNomeReal,
        tipoServico: tipoServico === 'Outros' ? tipoOutros.trim() : tipoServico,
        tipoOutros: tipoServico === 'Outros' ? tipoOutros.trim() : null,
        dadosEspecificos: dadosEspSalvos,
        observacoes: observacoes ?? null,
        status: novoStatus,
        ...(enderecoRaiz ? { endereco: enderecoRaiz } : {}),
        atualizadaEm: serverTimestamp(),
        ...coordsRaiz,
        ...(osId ? {} : { criadaEm: serverTimestamp() }),
      }), { merge: true });
      if (toast.isActive(toastId)) toast.close(toastId);

      // Detecta se ficou alguma foto/assinatura pendente no resultado real
      // (mais confiável que navigator.onLine, que pode mentir no Android)
      const temPendente = JSON.stringify(dadosEspSalvos || '').includes('"idbKey"')
        || (typeof dadosEspSalvos?.assinaturaCliente === 'string'
            && dadosEspSalvos.assinaturaCliente.startsWith('data:'));

      if (temPendente) {
        toast({
          title: novoStatus === 'aguardando_validacao' ? 'OS enviada (fotos pendentes)' : 'Rascunho salvo (fotos pendentes).',
          description: 'Fotos serão enviadas automaticamente quando houver conexão.',
          status: 'warning',
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: novoStatus === 'aguardando_validacao'
            ? 'OS enviada para validação!'
            : 'Rascunho salvo.',
          status: 'success',
          duration: 2500,
        });
      }
      navigate('/admin/tecnico/minhas-os');
    } catch (e) {
      if (toast.isActive(toastId)) toast.close(toastId);
      toast({ title: 'Erro ao salvar OS', description: e.message, status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
      // Quando online, tenta subir fotos que ficaram na fila de outras OS
      if (navigator.onLine) void syncPendingPhotos();
    }
  };

  if (loadingOS) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  // Reseta dadosEspecificos ao mudar o tipo de serviço (apenas OS nova)
  const handleTipoServicoChange = (novoTipo) => {
    setTipoServico(novoTipo);
    if (!osId) {
      if (novoTipo === 'Teste de Fumaça' || novoTipo === 'Inspeção de Rede') {
        setDadosEspecificos(FUMAÇA_PADRAO());
      } else if (novoTipo === 'Testes de Corante/Inspeção Ramal') {
        setDadosEspecificos(CORANTE_PADRAO());
      } else {
        setDadosEspecificos(null);
      }
    }
  };

  const STATUS_COLOR = {
    rascunho: 'gray', enviada: 'blue',
    aguardando_validacao: 'orange', aprovada: 'green', reprovada: 'red',
  };
  const STATUS_LABEL = {
    rascunho: 'Rascunho', enviada: 'Enviada',
    aguardando_validacao: 'Ag. Validação', aprovada: 'Aprovada', reprovada: 'Reprovada',
  };

  const isLocked = status === 'aprovada';

  return (
    <Flex direction="column" pt={{ base: '4px', md: '16px' }}
      gap="20px" maxW="860px" mx="auto" w="100%"
      pb={{ base: '88px', md: '16px' }}>

      {/* Cabeçalho */}
      <Flex align="center" justify="space-between" mb="4px">
        <Flex align="center" gap="10px">
          <IconButton
            icon={<Icon as={MdArrowBack} boxSize="20px" />}
            aria-label="Voltar"
            variant="ghost"
            borderRadius="12px"
            onClick={() => navigate(-1)}
          />
          <Heading size="md" color={textColor}>
            {osId ? 'Editar OS' : 'Nova Ordem de Serviço'}
          </Heading>
        </Flex>
        <Badge colorScheme={STATUS_COLOR[status] || 'gray'} borderRadius="full" px="3" py="1" fontSize="xs">
          {STATUS_LABEL[status] || status}
        </Badge>
      </Flex>

      {/* Banner de OS bloqueada */}
      {isLocked && (
        <Flex align="center" gap="10px" bg="green.50" _dark={{ bg: 'green.900' }}
          borderRadius="12px" p="14px" border="1px solid"
          borderColor="green.200" _dark_borderColor="green.700">
          <Icon as={MdLock} boxSize="20px" color="green.500" flexShrink={0} />
          <Box>
            <Text fontSize="sm" fontWeight="700" color="green.700" _dark={{ color: 'green.300' }}>
              OS Aprovada — somente leitura
            </Text>
            <Text fontSize="xs" color="green.600" _dark={{ color: 'green.400' }}>
              Esta OS foi aprovada e não pode mais ser editada pelo técnico. Solicite ao administrador caso precise de alterações.
            </Text>
          </Box>
        </Flex>
      )}

      {/* Dados do Serviço */}
      <Card p={{ base: '5', md: '7' }} borderWidth="1px" borderColor={cardBorder}>
        <Flex align="center" gap="10px" mb="5"
          pb="14px" borderBottom="1px solid" borderColor={cardBorder}>
          <Flex w="36px" h="36px" borderRadius="10px" bg="brand.50"
            _dark={{ bg: 'whiteAlpha.100' }}
            align="center" justify="center" flexShrink={0}>
            <Icon as={MdBuild} boxSize="18px" color="brand.500" />
          </Flex>
          <Box>
            <Text fontWeight="700" fontSize="sm" color={textColor}
              textTransform="uppercase" letterSpacing="wide" lineHeight="1.2">
              Dados do Serviço
            </Text>
            <Text fontSize="xs" color={subText} mt="1px">
              Identificação e agendamento da OS
            </Text>
          </Box>
        </Flex>
        <SimpleGrid columns={{ base: 1, md: 2 }} gap="18px">
          <Box>
            <FL color={textColor}>Cliente</FL>
            <Input value="SABESP" isReadOnly bg={inputBg} borderRadius="10px" fontWeight="600"
              fontSize={{ base: '16px', md: 'md' }} h={{ base: '44px', md: '40px' }} />
          </Box>
          <Box>
            <FL color={textColor}>Número da OS</FL>
            <Input
              placeholder="Ex: 2024/00123"
              value={numeroOS}
              onChange={(e) => setNumeroOS(e.target.value)}
              bg={inputBg} borderRadius="10px"
              inputMode="text" isDisabled={isLocked}
              fontSize={{ base: '16px', md: 'md' }} h={{ base: '44px', md: '40px' }}
            />
          </Box>
          <Box>
            <FL color={textColor}>Data do Atendimento</FL>
            <Input
              type="date"
              value={dataServico}
              onChange={(e) => setDataServico(e.target.value)}
              bg={inputBg} borderRadius="10px" isDisabled={isLocked}
              fontSize={{ base: '16px', md: 'md' }} h={{ base: '44px', md: '40px' }}
              sx={{ colorScheme: 'auto' }} // garante legibilidade no dark mode
            />
          </Box>
          <Box>
            <FL color={textColor}>Hora do Atendimento</FL>
            <Input
              type="time"
              value={hora}
              onChange={(e) => setHora(e.target.value)}
              bg={inputBg} borderRadius="10px" isDisabled={isLocked}
              fontSize={{ base: '16px', md: 'md' }} h={{ base: '44px', md: '40px' }}
              sx={{ colorScheme: 'auto' }}
            />
          </Box>

          <Box gridColumn={{ md: '1 / -1' }}>
            <FL color={textColor} required>Tipo de Serviço</FL>
            <TipoServicoField
              value={tipoServico}
              onChange={handleTipoServicoChange}
              outrosValue={tipoOutros}
              onOutrosChange={setTipoOutros}
              bg={inputBg}
              borderRadius="10px"
              fontSize={{ base: '16px', md: 'md' }}
              h={{ base: '44px', md: '40px' }}
              isDisabled={isLocked}
            />
          </Box>
        </SimpleGrid>
      </Card>

      {/* Formulário específico — Teste de Fumaça / Inspeção de Rede */}
      {(tipoServico === 'Teste de Fumaça' || tipoServico === 'Inspeção de Rede') && (
        <Box pointerEvents={isLocked ? 'none' : 'auto'} opacity={isLocked ? 0.65 : 1}>
          <TesteDeFumacaForm
            value={dadosEspecificos}
            onChange={setDadosEspecificos}
          />
        </Box>
      )}

      {/* Formulário específico — Teste de Corante / Inspeção de Ramal */}
      {tipoServico === 'Testes de Corante/Inspeção Ramal' && (
        <Box pointerEvents={isLocked ? 'none' : 'auto'} opacity={isLocked ? 0.65 : 1}>
          <TesteCoranteForm
            value={dadosEspecificos}
            onChange={setDadosEspecificos}
          />
        </Box>
      )}

      {/* Observações */}
      <Card p={{ base: '5', md: '7' }} borderWidth="1px" borderColor={cardBorder}>
        <Flex align="center" gap="10px" mb="5"
          pb="14px" borderBottom="1px solid" borderColor={cardBorder}>
          <Flex w="36px" h="36px" borderRadius="10px" bg="orange.50"
            _dark={{ bg: 'whiteAlpha.100' }}
            align="center" justify="center" flexShrink={0}>
            <Icon as={MdNotes} boxSize="18px" color="orange.500" />
          </Flex>
          <Box>
            <Text fontWeight="700" fontSize="sm" color={textColor}
              textTransform="uppercase" letterSpacing="wide" lineHeight="1.2">
              Observações
            </Text>
            <Text fontSize="xs" color={subText} mt="1px">
              Detalhes adicionais do atendimento
            </Text>
          </Box>
        </Flex>
        <Textarea
          placeholder="Descreva detalhes do serviço, ocorrências, materiais utilizados..."
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          rows={4} bg={inputBg} borderRadius="10px" resize="vertical"
          fontSize={{ base: '16px', md: 'md' }} isDisabled={isLocked}
        />
      </Card>

      {/* Botões — desktop (inline) */}
      <SimpleGrid display={{ base: 'none', md: 'grid' }}
        columns={2} gap="12px">
        <Button
          variant="outline"
          leftIcon={<Icon as={isLocked ? MdLock : MdSave} />}
          borderRadius="14px" h="52px" fontSize="md" fontWeight="600"
          isLoading={saving} isDisabled={isLocked}
          onClick={() => salvar('rascunho')}
        >
          Salvar rascunho
        </Button>
        <Button
          colorScheme="brand"
          leftIcon={<Icon as={isLocked ? MdLock : MdSend} />}
          borderRadius="14px" h="52px" fontSize="md" fontWeight="600"
          isLoading={saving} isDisabled={isLocked}
          onClick={() => salvar('aguardando_validacao')}
        >
          Enviar
        </Button>
      </SimpleGrid>

      {/* Botões — mobile sticky (fixo na base da tela) */}
      <Box
        display={{ base: 'block', md: 'none' }}
        position="fixed" bottom={0} left={0} right={0}
        zIndex={1001}
        bg={cardBg}
        borderTop="1px solid"
        borderColor={cardBorder}
        px="16px" pt="10px"
        pb="calc(10px + env(safe-area-inset-bottom, 0px))"
        boxShadow="0 -4px 20px rgba(0,0,0,0.08)"
      >
        <Flex gap="10px">
          <Button
            flex={1} variant="outline"
            leftIcon={<Icon as={isLocked ? MdLock : MdSave} />}
            borderRadius="12px" h="48px" fontSize="sm" fontWeight="600"
            isLoading={saving} isDisabled={isLocked}
            onClick={() => salvar('rascunho')}
          >
            Rascunho
          </Button>
          <Button
            flex={1} colorScheme="brand"
            leftIcon={<Icon as={isLocked ? MdLock : MdSend} />}
            borderRadius="12px" h="48px" fontSize="sm" fontWeight="600"
            isLoading={saving} isDisabled={isLocked}
            onClick={() => salvar('aguardando_validacao')}
          >
            Enviar OS
          </Button>
        </Flex>
      </Box>

    </Flex>
  );
}
